import { useMemo, useState } from "react";
import moment from "moment";
import XLSX from "xlsx-js-style";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

/**
 * A listagem nominal de faltas, debaixo das análises.
 *
 * ─── PORQUE EXISTE ──────────────────────────────────────────────────────────
 *
 * O relatório de faltas respondia a tudo menos à pergunta que se faz primeiro.
 * Dizia «taxa de 18%», «quarta-feira é o pior dia», «esquecimento é o motivo
 * mais comum» — e não dizia QUEM faltou, nem QUANDO. Para telefonar a alguém, ou
 * para conferir uma falta que o paciente contesta, não havia por onde começar.
 *
 * ─── UM COMPONENTE, DOIS RELATÓRIOS ─────────────────────────────────────────
 *
 * A v1 tem dois relatórios de faltas, um por tipo de agenda, e os dois ficheiros
 * são cópias de 1015 linhas um do outro. Escrever a tabela nos dois dava duas
 * tabelas a divergir à primeira correção. Fica aqui e os dois importam-na.
 *
 * ─── AQUI NÃO HÁ FILTRO DE DATAS ────────────────────────────────────────────
 *
 * O período escolhe-se no TOPO do relatório, e é esse que decide o que o
 * servidor manda. Repetir um intervalo de datas nesta secção dava dois filtros
 * de data no mesmo ecrã, a responder à mesma pergunta — e um relatório de
 * janeiro a mostrar uma contagem de março, sem se perceber qual dos dois manda.
 *
 * ─── A PROCURA É LOCAL, E ISSO É DE PROPÓSITO ───────────────────────────────
 *
 * A procura por nome trabalha sobre as faltas que já vieram, sem ir buscar
 * nada: quem escreve um nome quer a resposta enquanto escreve. Reconsultar o
 * servidor a cada letra daria um relatório lento e uma contagem a saltar.
 */

const COLUNAS_EXCEL = [
  "Data",
  "Hora",
  "Cliente",
  "Contacto",
  "Médico",
  "Tratamento / Categoria",
  "Motivo da falta",
  "Detalhe do motivo",
  "Unidade",
  "Origem",
];

/* O cabeçalho a negrito e com fundo, como nas outras folhas deste relatório. */
const ESTILO_CABECALHO = {
  font: { bold: true },
  fill: { fgColor: { rgb: "ECEFF1" } },
};

const semAcentos = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

function ListaFaltas({ faltas, nomeClinica, periodo, mostrarUnidade }) {
  const [procura, setProcura] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  const filtradas = useMemo(() => {
    /* Sem acentos e sem maiúsculas: procurar «ines» tem de encontrar «Inês»,
       senão o campo parece avariado a quem escreve sem acentos. */
    const alvo = semAcentos(procura.trim());
    if (!alvo) return faltas || [];

    return (faltas || []).filter((f) => semAcentos(f.cliente).includes(alvo));
  }, [faltas, procura]);

  /* Uma procura que reduz a lista a três linhas não pode deixar-nos na página
     7, a olhar para uma tabela vazia. */
  const paginaSegura = Math.min(
    pagina,
    Math.max(0, Math.ceil(filtradas.length / porPagina) - 1),
  );
  const visiveis = filtradas.slice(
    paginaSegura * porPagina,
    paginaSegura * porPagina + porPagina,
  );

  const exportar = () => {
    if (filtradas.length === 0) {
      toast.error("Não há faltas para exportar com estes filtros.");
      return;
    }

    try {
      const linhas = filtradas.map((f) => [
        moment(f.data).format("DD/MM/YYYY"),
        moment(f.data).format("HH:mm"),
        f.cliente,
        f.contacto || "",
        f.medico || "",
        f.tratamento || "",
        f.motivo || "",
        f.motivoDetalhes || "",
        mostrarUnidade ? f.unidadeCodigo || f.unidade || "" : "",
        f.origem || "",
      ]);

      const ws = XLSX.utils.aoa_to_sheet([COLUNAS_EXCEL, ...linhas]);

      ws["!cols"] = [
        { wch: 11 },
        { wch: 7 },
        { wch: 34 },
        { wch: 14 },
        { wch: 26 },
        { wch: 32 },
        { wch: 22 },
        { wch: 40 },
        { wch: 12 },
        { wch: 20 },
      ];
      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: linhas.length, c: COLUNAS_EXCEL.length - 1 },
        }),
      };
      /* A linha do cabeçalho fixa: numa lista de centenas de faltas, rolar sem
         cabeçalho obriga a contar colunas de cabeça. */
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };

      for (let c = 0; c < COLUNAS_EXCEL.length; c += 1) {
        const celula = ws[XLSX.utils.encode_cell({ r: 0, c })];
        if (celula) celula.s = ESTILO_CABECALHO;
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Faltas");

      XLSX.writeFile(
        wb,
        `Faltas_${semAcentos(nomeClinica).replace(/\s+/g, "-")}.xlsx`,
      );
    } catch (erro) {
      console.error("Erro ao exportar a lista de faltas:", erro);
      toast.error("Não foi possível gerar o ficheiro.");
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Faltas, uma a uma
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filtradas.length === (faltas || []).length
              ? `${filtradas.length} falta(s)`
              : `${filtradas.length} de ${(faltas || []).length} falta(s)`}
            {periodo ? ` · ${periodo}` : ""}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportar}
          disabled={filtradas.length === 0}
        >
          Exportar lista
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          label="Procurar cliente"
          placeholder="Nome do cliente"
          size="small"
          value={procura}
          onChange={(e) => {
            setProcura(e.target.value);
            setPagina(0);
          }}
          sx={{ minWidth: 240 }}
        />
        {/* O período é o do topo do relatório — ver a nota no cabeçalho. */}
        {procura && (
          <Button
            onClick={() => {
              setProcura("");
              setPagina(0);
            }}
          >
            Limpar
          </Button>
        )}
      </Box>

      {filtradas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {(faltas || []).length === 0
            ? "Não há faltas registadas no período escolhido."
            : "Nenhuma falta corresponde a estes filtros."}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Hora</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Médico</TableCell>
                  <TableCell>Tratamento</TableCell>
                  <TableCell>Motivo</TableCell>
                  {mostrarUnidade && <TableCell>Unidade</TableCell>}
                  <TableCell>Origem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiveis.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell>{moment(f.data).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>{moment(f.data).format("HH:mm")}</TableCell>
                    <TableCell>
                      {f.clienteId ? (
                        <Link
                          to={`/clientes/${f.clienteId}`}
                          style={{ textDecoration: "none" }}
                        >
                          {f.cliente}
                        </Link>
                      ) : (
                        f.cliente
                      )}
                    </TableCell>
                    <TableCell>{f.contacto}</TableCell>
                    <TableCell>{f.medico}</TableCell>
                    <TableCell>{f.tratamento}</TableCell>
                    <TableCell>
                      {f.motivo}
                      {f.motivoDetalhes && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {f.motivoDetalhes}
                        </Typography>
                      )}
                    </TableCell>
                    {mostrarUnidade && (
                      <TableCell>{f.unidadeCodigo || f.unidade}</TableCell>
                    )}
                    <TableCell>{f.origem}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtradas.length}
            page={paginaSegura}
            onPageChange={(e, nova) => setPagina(nova)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(e) => {
              setPorPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count}`
            }
          />
        </>
      )}
    </Paper>
  );
}

export default ListaFaltas;
